// SHELL DEL MODO "CREADOR DE VIDEO" (`/interfaz-v2/modo/creador-video`) —
// ADR-2026-09-07-modo-creador-video.
//
// El asistente en modo video, sin agente: un prompt, referencias opcionales y la
// ruedita de configuración de video (Formato · Calidad · Resolución · Duración). Segundo
// de los tres modos que reemplazan a "Crear desde cero".
//
// DOS estados en desktop, decididos por la columna de resultados y no por un flag:
//
//   • VACÍO (sin tandas): el layout del asistente — título centrado, la caja de prompt y
//     las tres cards de referencias.
//   • ACTIVO (hay tandas): la caja se ancla al pie "como en un chat" y todo lo demás es
//     la grilla de resultados (`PanelTandaCreaciones`, la misma columna de Modelo) a todo
//     el ancho y todo el alto, scrolleando sola. Las tandas SON el estado: cerrar el sheet
//     las descarta y la pantalla vuelve sola al vacío; reabrirlo con una corriendo abre
//     en activo.
//
// Mobile es el asistente en video: la caja con la ruedita, el botón Generar de ancho
// completo, y al generar la pantalla de carga de video y el salto a resultados.
//
// La generación no vive acá: la promesa se entrega a `InterfazV2` por
// `onGeneracionDelegada` con `esVideo: true` (la tanda de video ya existe por Animar
// desde el visor). El sheet reusa de Prompt libre la caja (`PromptBoxLibre`, con sus dos
// ranuras), la resolución de referencias y el ratio del formato; del asistente, la
// ruedita (`ConfiguracionMenu`, en `soloVideo`), las cards y los pickers.

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Film, Package, Plus, Sparkles, Upload, User } from 'lucide-react';
import LoadingAnimation from '../../../../components/LoadingAnimation';
import { Migas } from '../../../../components/Migas';
import {
  BORDE_PANTALLA,
  EncabezadoPantalla,
  MARCO_PANTALLA,
} from '../../../../components/PantallaChrome';
import type { FiltroContenido } from '../../../../utils/filtroContenido';
import {
  signReferenceImage,
  uploadReferenceImage,
  type ChatAttachment,
} from '../../agentAttachments';
import { useAgentEnv } from '../../agentEnvContext';
import { ProductFullMenu } from '../../ChatPanelV2';
import {
  ModelPickerSheet,
  modelSelectionLabel,
  type ModelSelection,
} from '../../ModelPicker';
import { useUserModels } from '../../modelsContext';
import { useUserProducts } from '../../productsContext';
import { getProductFullImageUrl } from '../../productsApi';
import { nombreCortoModo } from '../../modesCatalog';
import type { GeneracionDelegada } from '../../components/generacionDelegada';
import { PromptRecientesButton } from '../../components/PromptRecientesButton';
import { recordPromptHistory } from '../../promptHistory';
import { AccionCardsRow } from '../asistente/AccionCardsRow';
import { CardsDeReferencia } from '../shared/CardsDeReferencia';
import { ConfiguracionMenu } from '../asistente/ConfiguracionMenu';
import { ReferenciasSheet } from '../asistente/ReferenciasSheet';
import { useIsMdUp } from '../asistente/useIsMdUp';
import { usePegarYSoltarImagenes, VeloSoltarImagen } from '../shared/usePegarYSoltarImagenes';
import type { VideoQuality } from '../asistente/directMode';
import type { VideoResolution } from '../../../../utils/credits';
import { PromptBoxLibre } from '../promptLibre/PromptBoxLibre';
import { ratioDelFormatoElegido, resolverReferencias } from '../promptLibre/promptLibre';
import { MAX_REFERENCIAS_VIDEO } from '../../referenciasDeGeneracion';
import { estimarDuracionVideoMs } from '../../../../utils/estimacionVideo';
import { miniaturaDeAdjunto } from '../../etiquetaDeAdjunto';
import { VisorDeAdjunto } from './VisorDeAdjunto';
import { CatalogoDeTipoDeVideo, MenuDeTipos, TipoDeVideoAnclado } from './TipoDeVideoPill';
import { usePosicionSobreElBoton } from '../../components/posicionSobreElBoton';
import { GENERAL, esGeneral } from '../../videoTiposCatalog';
import {
  AJUSTES_VIDEO_DEFAULT,
  FORMATO_DEFAULT_VIDEO,
  PLACEHOLDER_CON_TIPO,
  PLACEHOLDER_GENERAL,
  conCalidad,
  creditosCreadorVideo,
  faltaCreadorVideo,
  idsDeVideo,
  type AjustesVideo,
} from './creadorVideo';

// La vuelta atrás del modo viaja en la entrada de historial
// (ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste).
import {
  configDelModoAlMontar,
  recordarConfigDelModo,
} from '../shared/volverAlModo';
import { palabraDeModeloActual } from '../../../../lib/nombreDeModelo';

/** La pantalla de resultados de mobile — la misma a la que salta el asistente. */
const RUTA_RESULTADOS = '/redes-sociales/imagenes-generadas';
/** El `path` del modo en el catálogo. */
const PATH_MODO = 'creador-video';

/** La ruta del modo — la entrada de historial que se sella al irse a los resultados. */
const RUTA_MODO = '/interfaz-v2/modo/creador-video';

/** Lo que hace falta para reabrir el modo tal como se generó —
 *  ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste.
 *  Los ADJUNTOS no van: son archivos con su preview local, no un dato serializable. */
interface ConfigDeCreadorVideo {
  prompt: string;
  formatSlug: string;
  ajustes: AjustesVideo;
}
/** Barra de la pantalla de carga de video en mobile cuando todavía no hay una tanda
 *  con su estimación (no debería pasar: se setea al generar). */
const DURACION_BARRA_VIDEO_RESPALDO_MS = 8 * 60_000;
/** Lo que mide la capa de la caja de prompt vacía, en px: el valor con el que arranca el
 *  padding de la grilla hasta que el `ResizeObserver` mide el real. */
const ALTO_CAJA_INICIAL = 184;
/**
 * Dónde arranca la grilla, en px, para que su primera fila quede DEBAJO de la pastilla
 * del breadcrumb: 16px de aire arriba + 36px de pastilla + 20px de respiro.
 *
 * Es aire y no un tope duro: la grilla sigue pasando por detrás de la pastilla cuando se
 * scrollea, que es todo el punto.
 */
const ALTO_PILLS = 72;

export interface CreadorVideoSheetProps {
  animated: boolean;
  sidebarWidth: number;
  onClose: () => void;
  /** La grilla de resultados que arma `InterfazV2`, o `null` sin tandas. Decide el estado. */
  columnaResultados: React.ReactNode | null;
  onGeneracionDelegada: (g: GeneracionDelegada) => void;
  /** `checkGenerationGuards('video', costo)` del padre: piso Pro/Ultra + saldo. */
  verificarGuards: (costo: number) => Promise<boolean>;
  /** El visor de una creación está abierto: el sheet baja su z-index para no taparlo. */
  cederZIndex?: boolean;
  /** Qué muestra la grilla — ADR-2026-09-08-creador-de-video-filtra-por-tipo-de-contenido.
   *  El estado vive en `InterfazV2`, que es quien arma `columnaResultados`. */
  filtro: FiltroContenido;
  onFiltroChange: (filtro: FiltroContenido) => void;
  /**
   * "Ver videos creados" del estado vacío de escritorio: el padre arma `columnaResultados`
   * aunque no haya tandas, y el sheet pasa al estado activo con la grilla del historial
   * (ADR-2026-09-19-el-creador-de-video-arranca-en-15-segundos-y-abre-sus-creaciones).
   */
  onVerCreaciones: () => void;
}

/**
 * Las pastillas que flotan sobre la grilla del estado activo —
 * ADR-2026-09-08-el-chrome-del-creador-de-video-flota-sobre-la-grilla.
 *
 * Vidrio: la grilla pasa por debajo y se sigue adivinando, así queda claro que la
 * pastilla está ENCIMA de una lista que sigue para arriba y no apoyada sobre una franja
 * vacía. El anillo finito es lo único que la separa de una foto clara.
 */
const PASTILLA_FLOTANTE =
  'pointer-events-auto rounded-full bg-white/85 backdrop-blur-md ring-1 ring-black/[0.06] shadow-[0_2px_10px_rgba(2,6,43,0.08)]';

/**
 * El segmentado "Videos · Todo".
 *
 * Dos opciones y no un desplegable: son dos, entran, y así el estado se lee sin abrir
 * nada — que es lo que importa en un filtro que arranca PRENDIDO y por lo tanto tiene que
 * explicar solo por qué la grilla no muestra todo.
 *
 * El activo va en el azul suave de la app (`#1644EB14` + texto de marca), el mismo par
 * que usan los íconos de los menús: sobre fotos, un bloque de azul sólido pesaba más que
 * las cards que tiene detrás.
 */
const FiltroPill: React.FC<{
  valor: FiltroContenido;
  onChange: (f: FiltroContenido) => void;
}> = ({ valor, onChange }) => (
  <div
    role="group"
    aria-label="Qué mostrar"
    className={`${PASTILLA_FLOTANTE} inline-flex items-center gap-0.5 p-1`}
  >
    {(
      [
        { id: 'videos', label: 'Videos' },
        { id: 'todo', label: 'Todo' },
      ] as const
    ).map((op) => {
      const activo = valor === op.id;
      return (
        <button
          key={op.id}
          type="button"
          aria-pressed={activo}
          onClick={() => onChange(op.id)}
          // `py-1.5 px-3`: el padding horizontal es el doble del vertical, como el resto
          // de los botones de la app.
          className={`rounded-full py-1.5 px-3 text-[13px] font-medium leading-tight transition-colors ${
            activo
              ? 'bg-[#1644EB14] text-brand-primary'
              : 'text-black/45 hover:text-black/75'
          }`}
        >
          {op.label}
        </button>
      );
    })}
  </div>
);

/** Fila del menú "+" del estado activo (la misma forma que el menú de adjuntos del
 *  asistente). */
const ItemMenu: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-neutral-50 active:bg-neutral-100 text-left transition-colors"
  >
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#1644EB1A] text-brand-primary shrink-0">
      {icon}
    </span>
    <span className="text-[14.5px] text-brand-black/90">{label}</span>
  </button>
);

export const CreadorVideoSheet: React.FC<CreadorVideoSheetProps> = ({
  animated,
  sidebarWidth,
  onClose,
  columnaResultados,
  onGeneracionDelegada,
  verificarGuards,
  cederZIndex = false,
  filtro,
  onFiltroChange,
  onVerCreaciones,
}) => {
  const navigate = useNavigate();
  const isMdUp = useIsMdUp();
  const { userId, companyId, refreshModels } = useAgentEnv();
  const userProducts = useUserProducts();
  const userModels = useUserModels();

  // ── Estado del modo ──────────────────────────────────────────────────────
  /**
   * Lo que se estaba generando cuando se salió, si el "Atrás" lo trajo de vuelta —
   * ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste.
   */
  const configAlVolver = useRef(configDelModoAlMontar<ConfigDeCreadorVideo>()).current;
  const [prompt, setPrompt] = useState(configAlVolver?.prompt ?? '');
  const [adjuntos, setAdjuntos] = useState<ChatAttachment[]>([]);
  // El tipo de video elegido en la pill. Default General; vive en la sesión del sheet,
  // como el prompt (ADR-2026-09-18-el-creador-de-video-escribe-el-prompt-con-skills-por-tipo).
  const [tipoVideo, setTipoVideo] = useState<string>(GENERAL.slug);
  // El menú del "+" va portaleado al body y posicionado sobre su botón
  // (ADR-2026-09-20-la-fila-del-composer-scrollea-y-sus-menus-se-portalean).
  const masBtnRef = useRef<HTMLButtonElement>(null);
  // Pacing de la barra de la ÚLTIMA tanda lanzada (mobile): se fija al generar con la
  // configuración de ese pedido (ADR-2026-09-18-la-barra-del-video-se-pacea-con-lo-que-tarda-de-verdad).
  const [duracionBarraMs, setDuracionBarraMs] = useState(DURACION_BARRA_VIDEO_RESPALDO_MS);
  const [formatSlug, setFormatSlug] = useState<string>(
    configAlVolver?.formatSlug ?? FORMATO_DEFAULT_VIDEO,
  );
  const [ajustes, setAjustes] = useState<AjustesVideo>(
    configAlVolver?.ajustes ?? AJUSTES_VIDEO_DEFAULT,
  );
  const [configMenuOpen, setConfigMenuOpen] = useState(false);
  const [masOpen, setMasOpen] = useState(false);
  // El catálogo de tipos abierto desde el "+" del estado activo (ver `masMenu`).
  const [tiposOpen, setTiposOpen] = useState(false);
  const posMas = usePosicionSobreElBoton(masBtnRef, masOpen, 250);
  const [picker, setPicker] = useState<'none' | 'product' | 'model'>('none');
  const [referenciasOpen, setReferenciasOpen] = useState(false);
  /** El adjunto abierto en la vista ampliada, o `null`. */
  const [adjuntoAbierto, setAdjuntoAbierto] = useState<ChatAttachment | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ── Mobile: pantalla de carga y salto a resultados ───────────────────────
  const [cargando, setCargando] = useState(false);
  const [completar, setCompletar] = useState(false);
  const pendingNavRef = useRef<{ imageIds: string[] } | null>(null);
  const vivoRef = useRef(true);
  useEffect(() => {
    vivoRef.current = true;
    return () => {
      vivoRef.current = false;
    };
  }, []);

  const creditos = creditosCreadorVideo(ajustes);
  const listo = !!userId && !!companyId;
  const falta = faltaCreadorVideo({
    prompt,
    listo,
    tipoVideo,
    cantidadAdjuntos: adjuntos.length,
  });
  const activo = isMdUp && !!columnaResultados;

  /**
   * Cuánto mide la capa de la caja de prompt, para que la grilla que le pasa POR DEBAJO
   * pueda scrollearse hasta el final — ADR-2026-09-08-el-chrome-del-creador-de-video-flota-sobre-la-grilla.
   *
   * Se MIDE y no se constantea: la caja crece con el textarea (hasta varias líneas) y con
   * la tira de referencias, y con un valor fijo la última fila de cards quedaba tapada
   * justo cuando el usuario escribía un prompt largo — o sea, siempre que hubiera algo
   * que mirar.
   *
   * El default es lo que mide vacía: es el valor correcto para el primer frame, antes de
   * que el observer reporte.
   */
  const cajaRef = useRef<HTMLDivElement | null>(null);
  const [altoCaja, setAltoCaja] = useState(ALTO_CAJA_INICIAL);
  useEffect(() => {
    const nodo = cajaRef.current;
    if (!nodo || !activo) return;
    const ro = new ResizeObserver(([entrada]) => {
      // `borderBoxSize` incluye el padding de la capa, que es el aire real entre la
      // última fila y el borde de abajo.
      const alto = entrada?.borderBoxSize?.[0]?.blockSize ?? nodo.offsetHeight;
      if (alto > 0) setAltoCaja(alto);
    });
    ro.observe(nodo);
    return () => ro.disconnect();
  }, [activo]);

  // ── Mobile: el sheet se fija al visual viewport con el teclado abierto ───
  // Mismo mecanismo que el asistente y Prompt libre (ver el comentario allá).
  const [vvStyle, setVvStyle] = useState<React.CSSProperties>({});
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let baseline = 0;
    let lastWidth = 0;
    const apply = () => {
      if (window.innerWidth >= 768) {
        setVvStyle({});
        return;
      }
      if (lastWidth !== window.innerWidth) {
        lastWidth = window.innerWidth;
        baseline = vv.height;
      }
      if (vv.height > baseline) baseline = vv.height;
      const kb = Math.max(0, baseline - vv.height);
      setVvStyle(
        kb > 1
          ? {
              top: `${Math.max(0, vv.offsetTop)}px`,
              height: `${vv.height}px`,
              bottom: 'auto',
            }
          : {},
      );
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    window.addEventListener('resize', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
    };
  }, []);

  // ── Referencias (las mismas reglas que Prompt libre) ─────────────────────
  const quitarAdjunto = (id: string) =>
    setAdjuntos((prev) => prev.filter((a) => a.id !== id));

  const agregarProductos = (ids: string[]) => {
    setAdjuntos((prev) => {
      const ya = new Set(
        prev
          .filter((a): a is Extract<ChatAttachment, { kind: 'product' }> => a.kind === 'product')
          .map((a) => a.productId),
      );
      const nuevos: ChatAttachment[] = [];
      for (const id of ids) {
        if (ya.has(id)) continue;
        const p = userProducts.find((x) => x.id === id);
        if (!p) continue;
        nuevos.push({
          kind: 'product',
          id: `prod-${id}`,
          productId: id,
          name: p.name,
          imageUrl: p.imageUrl,
        });
      }
      return [...prev, ...nuevos];
    });
    setPicker('none');
  };

  const elegirModelo = (sel: ModelSelection) => {
    setAdjuntos((prev) => [
      ...prev.filter((a) => a.kind !== 'model'),
      {
        kind: 'model',
        id: 'model-attachment',
        selection: sel,
        name: modelSelectionLabel(sel),
        imageUrl:
          sel.mode === 'preset'
            ? sel.imageUrl
            : sel.mode === 'upload'
              ? sel.previewUrl
              : null,
      },
    ]);
    setPicker('none');
  };

  const subirImagen = async (file: File | null) => {
    if (!file || !companyId) return;
    setSubiendo(true);
    try {
      const { storagePath, previewUrl } = await uploadReferenceImage(file, companyId);
      if (!vivoRef.current) return;
      setAdjuntos((prev) => [
        ...prev,
        { kind: 'image', id: `img-${Date.now()}`, storagePath, previewUrl },
      ]);
    } catch (e) {
      console.error('[CreadorVideo] subir imagen falló', e);
    } finally {
      if (vivoRef.current) setSubiendo(false);
    }
  };

  // Pegar (Ctrl/Cmd+V) y soltar (drag and drop) imágenes en cualquier punto de la
  // pantalla, además del "+" — ADR-2026-09-19-pegar-y-soltar-imagenes-en-la-caja-de-prompt.
  // De a una y en orden, así el "subiendo" de la caja refleja la que está en vuelo.
  const { arrastrando } = usePegarYSoltarImagenes({
    /* Sólo con los pickers CERRADOS —
       ADR-2026-09-21-pegar-y-soltar-no-escucha-con-un-picker-abierto.

       Los listeners viven en `document`, así que con el picker de modelos abierto
       —que tiene su propio "Subir imagen del modelo"— pegar o soltar una foto la
       subía como referencia del modo de ATRÁS, callado y detrás del panel abierto.
       El picker no tiene handler propio de paste/drop, así que no se le robaba nada;
       lo que estaba mal era el destino. */
    activo: picker === 'none',
    onImagenes: (archivos) => {
      void (async () => {
        for (const f of archivos) await subirImagen(f);
      })();
    },
  });

  // ── Generar ──────────────────────────────────────────────────────────────
  const handleGenerar = async () => {
    if (falta || cargando || !userId || !companyId) return;
    // Guards de VIDEO (piso Pro/Ultra + saldo con lo comprometido) antes de pedir
    // nada: si no pasa, el padre ya mostró su modal.
    const ok = await verificarGuards(creditos);
    if (!ok || !vivoRef.current) return;

    // Lo que viaja se captura AHORA: el prompt y las referencias se quedan en pantalla
    // para iterar, y lo que se cambie después no entra en esta tanda.
    const executionId = crypto.randomUUID();
    const promptFinal = prompt.trim();
    const adjuntosDeLaTanda = adjuntos;
    // Los productos adjuntos, por id: las URLs de `resolverReferencias` alcanzan para que
    // el modelo VEA la foto, pero no para que la fila sepa de qué producto era — y sin eso
    // el video no caía en ninguna marca (ADR-2026-09-13-brand-id-en-imagenes-generadas).
    const variantIdsDeLaTanda = adjuntosDeLaTanda.flatMap((a) =>
      a.kind === 'product' ? [a.productId] : [],
    );
    const ajustesDeLaTanda = ajustes;
    const tipoVideoDeLaTanda = tipoVideo;
    // Cuánto va a tardar, según calidad, resolución y duración: pacea la barra del
    // recuadro en desktop y la pantalla de carga en mobile.
    const duracionEstimadaMs = estimarDuracionVideoMs({
      quality: ajustesDeLaTanda.quality,
      resolution: ajustesDeLaTanda.resolution,
      durationSeconds: ajustesDeLaTanda.durationSeconds,
    });
    setDuracionBarraMs(duracionEstimadaMs);
    const formatoDeLaTanda = formatSlug;
    const userIdFinal = userId;
    const companyIdFinal = companyId;

    // El prompt se guarda para el menú "Recientes"
    // (ADR-2026-09-08-creador-de-video-recuerda-sus-prompts). Va ACÁ y no adentro de la
    // promesa de generación: lo que interesa es qué escribió el usuario, y un video que
    // falla del lado del backend no vuelve el prompt menos reusable —al contrario, es
    // justo el que va a querer volver a tirar. Es fire-and-forget: nunca demora ni rompe
    // una generación.
    // Con tipo elegido el prompt puede ir vacío: nada que recordar.
    if (promptFinal) recordPromptHistory(companyIdFinal, 'video', promptFinal);

    const resultado = (async () => {
      // Las referencias viajan ETIQUETADAS, como las de imagen: el backend usa el tipo
      // para nombrarlas en el prompt (`@Image1 es el producto…`), que es la única forma
      // que tiene el proveedor de video de saber qué es cada foto
      // (ADR-2026-09-12-las-referencias-del-video-se-nombran-en-el-prompt).
      //
      // El tope es el del VIDEO (6) y no el de imagen (10): recortar acá tira el fondo
      // antes que el producto, mientras que pasarse lo único que conseguía era un 400 de
      // la ruta que se llevaba puesta la tanda entera
      // (ADR-2026-09-12-el-video-recortaba-en-la-ruta-y-no-en-la-pantalla).
      const referenceImages = await resolverReferencias(
        adjuntosDeLaTanda,
        {
          productoUrl: (id) => getProductFullImageUrl(companyIdFinal, id),
          modeloUrl: (id) =>
            userModels.find((m) => String(m.id) === id)?.thumbnailUrl ?? null,
          adjuntoUrl: (path, bucket) => signReferenceImage(path, bucket),
        },
        MAX_REFERENCIAS_VIDEO,
      );
      // `import()` dinámico, como la card del chat: el motor no entra al bundle
      // principal por esta pantalla.
      const { generateVideoFromPrompt } = await import('../../agentGenerate');
      const { videoId } = await generateVideoFromPrompt({
        userId: userIdFinal,
        prompt: promptFinal,
        formatSlug: formatoDeLaTanda,
        durationSeconds: ajustesDeLaTanda.durationSeconds,
        quality: ajustesDeLaTanda.quality,
        resolution: ajustesDeLaTanda.resolution,
        companyId: companyIdFinal,
        executionId,
        modoUtilizado: 'creador_video',
        tipoVideo: tipoVideoDeLaTanda,
        ...(variantIdsDeLaTanda.length > 0 ? { variantIds: variantIdsDeLaTanda } : {}),
        ...(referenceImages.length > 0 ? { referenceImages } : {}),
      });
      // El backend ya descontó los créditos: el pill del shell escucha este evento.
      try {
        window.dispatchEvent(new Event('atlas:credits-updated'));
      } catch {
        /* noop */
      }
      return idsDeVideo(videoId);
    })();

    onGeneracionDelegada({
      tipo: 'creador-video',
      total: 1,
      ratio: ratioDelFormatoElegido(formatoDeLaTanda),
      producto: '',
      esVideo: true,
      creditos,
      duracionEstimadaMs,
      // Con el id la tanda se reanuda tras recargar: el reanudador cierra las de video
      // con `preloadVideosDeTanda` (ADR-2026-09-06-registro-de-generaciones-en-curso).
      executionIds: [executionId],
      resultado,
    });

    // Mobile: no hay grilla donde ver la tanda → pantalla de carga de video y, al
    // terminar con fila, la pantalla de resultados. El error ya lo mostró el padre.
    if (!isMdUp) {
      pendingNavRef.current = null;
      setCompletar(false);
      setCargando(true);
      resultado.then(
        (ids) => {
          if (!vivoRef.current) return;
          pendingNavRef.current = ids.length > 0 ? { imageIds: ids } : null;
          // La vuelta atrás: el "Atrás" desde los resultados devuelve el prompt y los
          // ajustes con los que se pidió el video, no un modo en blanco.
          recordarConfigDelModo<ConfigDeCreadorVideo>(navigate, RUTA_MODO, {
            prompt,
            formatSlug,
            ajustes,
          });
          setCompletar(true);
        },
        () => {
          if (!vivoRef.current) return;
          setCargando(false);
          setCompletar(false);
        },
      );
    }
  };

  // ── Piezas ───────────────────────────────────────────────────────────────
  const titulo = nombreCortoModo(PATH_MODO, 'Creador de video');

  const referenciasThumbs = adjuntos.map((a) => ({
    id: a.id,
    url: miniaturaDeAdjunto(a),
    label: a.kind === 'image' ? 'Imagen' : a.name,
  }));

  // La ruedita del asistente, en sólo-video: Formato, Calidad, Resolución y Duración.
  // `outputKind` fijo y su cambio sin efecto: el toggle no se dibuja.
  const ruedita = (
    <ConfiguracionMenu
      outputKind="video"
      onChangeOutputKind={() => {}}
      formatSlug={formatSlug}
      onChangeFormat={setFormatSlug}
      cantidad={1}
      onChangeCantidad={() => {}}
      durationSeconds={ajustes.durationSeconds}
      onChangeDuration={(s) => setAjustes((a) => ({ ...a, durationSeconds: s }))}
      videoQuality={ajustes.quality}
      onChangeVideoQuality={(q: VideoQuality) => setAjustes((a) => conCalidad(a, q))}
      videoResolution={ajustes.resolution}
      onChangeVideoResolution={(r: VideoResolution) =>
        setAjustes((a) => ({ ...a, resolution: r }))
      }
      open={configMenuOpen}
      onOpenChange={setConfigMenuOpen}
      soloVideo
    />
  );

  // El "+" del estado activo: las tres referencias, sin las cards (que ahí no están).
  const masMenu = (
    <div className="relative shrink-0">
      <button
        ref={masBtnRef}
        type="button"
        onClick={() => setMasOpen((v) => !v)}
        aria-label="Añadir referencia"
        aria-expanded={masOpen}
        // Pill con ícono + rótulo en desktop, redonda de sólo ícono en mobile: la misma
        // forma exacta que la ruedita de al lado, que ya resolvió esto
        // (ADR-2026-09-08-los-adjuntos-de-la-caja-dicen-que-son-y-se-abren). Un "+"
        // pelado al lado de un "Configuración" rotulado no se lee como su par.
        className={`inline-flex items-center justify-center gap-1.5 h-9 w-9 md:w-auto md:px-3.5 rounded-full transition-colors ${
          masOpen
            ? 'bg-neutral-200 text-[#02062BE6]'
            : 'bg-neutral-100 hover:bg-neutral-200 text-[#02062BB3]'
        }`}
      >
        <Plus className="w-4 h-4 shrink-0" strokeWidth={2.25} />
        <span className="hidden md:inline text-[13.5px] font-medium whitespace-nowrap">
          Añadir
        </span>
      </button>
      {masOpen &&
        createPortal(
          <>
          <div className="fixed inset-0 z-[55]" onClick={() => setMasOpen(false)} />
          {/* Abre hacia ARRIBA desde el botón, PORTALEADO: la fila del composer scrollea
              en horizontal y un menú `absolute` quedaría recortado
              (ADR-2026-09-20-la-fila-del-composer-scrollea-y-sus-menus-se-portalean). */}
          <div
            className="fixed w-[250px] max-w-[calc(100vw-16px)] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-1.5 z-[56]"
            style={
              posMas ? { left: posMas.left, bottom: posMas.bottom } : { visibility: 'hidden' }
            }
          >
            <ItemMenu
              icon={<Upload className="w-4 h-4" />}
              label="Subir imagen"
              onClick={() => {
                setMasOpen(false);
                fileInputRef.current?.click();
              }}
            />
            <ItemMenu
              icon={<Package className="w-4 h-4" />}
              label="Añadir producto"
              onClick={() => {
                setMasOpen(false);
                setPicker('product');
              }}
            />
            <ItemMenu
              icon={<User className="w-4 h-4" />}
              label={`Añadir ${palabraDeModeloActual().singular}`}
              onClick={() => {
                setMasOpen(false);
                setPicker('model');
              }}
            />
            {/* EL TIPO DE VIDEO, sólo con General puesto
                (ADR-2026-09-24-en-el-estado-activo-el-catalogo-vive-en-el-mas). En el
                estado activo la pantalla es la grilla de resultados: el catálogo de abajo
                no existe y el único control es la imagen anclada de la caja, que con
                General no dibuja nada —no hay tipo que anclar—. Sin esta fila, después de
                generar (o de tocar la X de un tipo) no había forma de elegir uno para la
                siguiente tanda sin salir del modo. Con un tipo puesto no se ofrece: la
                puerta es el anclado, y dos puertas a lo mismo en la misma fila es ruido. */}
            {esGeneral(tipoVideo) && (
              <ItemMenu
                icon={<Film className="w-4 h-4" />}
                label="Tipo de video"
                onClick={() => {
                  setMasOpen(false);
                  setTiposOpen(true);
                }}
              />
            )}
          </div>
          </>,
          document.body,
        )}
    </div>
  );

  // Pastilla de créditos: el precio es determinista, así que se anuncia antes de tocar
  // Generar (la regla de "el video nunca se genera sin ver el costo"). Sólo cuando se
  // puede generar (`!falta`): con prompt, o con tipo elegido y adjuntos
  // (ADR-2026-09-18-el-creador-de-video-escribe-el-prompt-con-skills-por-tipo). En mobile
  // va adentro del botón grande.
  const pastillaCreditos =
    !falta ? (
      <span className="hidden md:inline-flex shrink-0 items-center gap-1.5 h-7 px-2.5 rounded-full bg-neutral-100 text-[12.5px] font-medium text-[#02062BB3] whitespace-nowrap tabular-nums">
        {/* El mismo ícono de créditos del botón de mobile y del pill del shell. */}
        <img
          src="/images/token_icon.png"
          alt=""
          aria-hidden="true"
          className="w-3.5 h-3.5 object-contain"
        />
        {creditos} {creditos === 1 ? 'crédito' : 'créditos'}
      </span>
    ) : null;

  const box = (
    <PromptBoxLibre
      prompt={prompt}
      onPromptChange={setPrompt}
      onSubmit={handleGenerar}
      adjuntos={adjuntos}
      onQuitarAdjunto={quitarAdjunto}
      subiendo={subiendo}
      // Mobile, estado vacío: las referencias ya se ven en la tira de la card.
      mostrarChips={isMdUp}
      // El rótulo y la vista ampliada son de ESTE modo por ahora: la caja es compartida
      // con Prompt libre (ADR-2026-09-08-los-adjuntos-de-la-caja-dicen-que-son-y-se-abren).
      conTipo
      onAbrirAdjunto={setAdjuntoAbierto}
      onFocusChange={setInputFocused}
      /* El tipo elegido, anclado arriba en la fila de adjuntos, en vez de la pill que
         decía "General" en la fila de abajo
         (ADR-2026-09-24-el-tipo-elegido-se-ancla-a-la-caja). Con General no dibuja nada.
         Sigue siendo el control: tocarlo reabre el catálogo y la X vuelve a General.
         Dirige al escritor de prompts del backend y, con adjuntos, permite generar sin
         escribir (ADR-2026-09-18-el-creador-de-video-escribe-el-prompt-con-skills-por-tipo). */
      anclado={<TipoDeVideoAnclado valor={tipoVideo} onChange={setTipoVideo} />}
      placeholder={
        esGeneral(tipoVideo) && adjuntos.length === 0 ? PLACEHOLDER_GENERAL : PLACEHOLDER_CON_TIPO
      }
      izquierda={
        <>
          {ruedita}
          {activo && masMenu}
          {/* "Recientes": los prompts de video ya usados
              (ADR-2026-09-08-creador-de-video-recuerda-sus-prompts). Se esconde solo
              mientras no haya ninguno, así que en una cuenta nueva la fila queda como
              estaba. `alinear="izquierda"` porque vive de este lado de la caja: por
              derecha, la lista de 380px se abriría hacia afuera. */}
          <PromptRecientesButton
            tipo="video"
            variante="pill"
            alinear="izquierda"
            titulo="Prompts que usaste"
            onPick={setPrompt}
          />
        </>
      }
      derecha={pastillaCreditos}
      conEnviar={isMdUp}
      enviarDeshabilitado={!!falta}
    />
  );

  const tituloGrande = (
    <h1 className="text-[32px] md:text-[44px] font-normal text-center leading-tight text-brand-black">
      <span className="block font-sans font-semibold">Creá</span>
      <span className="block font-display italic">tu video</span>
    </h1>
  );

  // MOBILE: la card única que abre el sheet de referencias, con las miniaturas de lo ya
  // elegido al lado.
  const cards = (
    <AccionCardsRow
      onOpenReferencias={() => setReferenciasOpen(true)}
      referencias={referenciasThumbs}
      onRemoveReferencia={quitarAdjunto}
    />
  );

  // DESKTOP, estado vacío: las tres cards. Iban adentro de `AccionCardsRow` hasta que esa
  // pieza pasó a ser sólo mobile (2026-09-09) y esta pantalla se quedó sin ninguna forma
  // de adjuntar en escritorio: el "+" de la caja recién aparece con `activo`, o sea
  // DESPUÉS de generar algo
  // (ADR-2026-09-13-las-cards-de-referencia-de-escritorio-vuelven-como-pieza-de-los-modos).
  const cardsDesktop = (
    <CardsDeReferencia
      onAddProduct={() => setPicker('product')}
      onAddModel={() => setPicker('model')}
      onUploadImage={() => fileInputRef.current?.click()}
      uploading={subiendo}
    />
  );

  // DESKTOP, estado vacío: el catálogo de tipos ABIERTO, debajo de las tres cards
  // (ADR-2026-09-24-los-tipos-de-video-se-ven-en-la-pantalla). Es el mismo contenido del
  // menú de la pill y el mismo estado (`tipoVideo` / `setTipoVideo`): elegir acá mueve la
  // pill de la caja, y al revés. La pill NO se va —sigue siendo el control en el estado
  // activo, donde esta pantalla es la grilla y no hay dónde apoyar el catálogo.
  // A TODO EL ANCHO de la pantalla, no al de la caja: en `fila` las nueve cards se reparten
  // el espacio (ADR-2026-09-24-el-catalogo-de-tipos-va-en-una-fila). Con el tope de 680 px
  // de la caja, las secciones se apilaban en tres filas y sobraba media pantalla vacía a la
  // derecha, que es de lo único que este bloque tiene de sobra.
  //
  // En el TELÉFONO se ve el mismo bloque (pedido del usuario) pero con la disposición
  // `angosta`: la grilla de 2 columnas que ya usaba el bottom sheet de la pill. `fila`
  // ahí no sirve —las secciones se parten y cada card queda de 96px— y es justo la
  // disposición que este catálogo ya tenía resuelta para un ancho de teléfono.
  //
  // El título dejó de ser un rótulo en versalitas y pasó a ser un título de verdad
  // (pedido del usuario): 18px, negro y sin mayúsculas. Los rótulos de cada sección
  // —"Automático", "UGC"— siguen en versalitas, que es lo que los hace leerse como
  // subdivisiones de éste y no como títulos hermanos.
  const bloqueDeTipos = (disposicion: 'fila' | 'angosta') => (
    <section className="flex w-full flex-col gap-3">
      <h2 className="text-[18px] font-semibold text-brand-black">Tipo de video</h2>
      <CatalogoDeTipoDeVideo valor={tipoVideo} onChange={setTipoVideo} disposicion={disposicion} />
    </section>
  );

  return ReactDOM.createPortal(
    <>
      {/* Backdrop — el del asistente: invisible en mobile, velo en desktop a partir de
          la sidebar. */}
      <div
        className={`fixed inset-0 md:left-[var(--sidebar-w,0px)] z-30 bg-black/0 md:bg-black/30 md:rounded-tl-[26px] md:rounded-bl-[26px] ${MARCO_PANTALLA} transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ '--sidebar-w': `${sidebarWidth}px` } as React.CSSProperties}
        onClick={onClose}
      />
      {/* Sheet: el del asistente ("básicamente igual al crear desde cero"). Mobile:
          inset-0 debajo del chrome del shell; desktop: a la derecha de la sidebar con el
          marco de las pantallas de modo. Con el visor abierto cede el z-index. */}
      <div
        className={`fixed inset-0 bg-surface-mobile md:bg-surface-content flex flex-col transition-[transform,opacity] duration-300 ease-out md:left-[var(--sidebar-w,0px)] md:rounded-tl-[26px] md:rounded-bl-[26px] md:overflow-hidden ${
          cederZIndex ? 'z-40 md:z-30' : 'z-40'
        } ${MARCO_PANTALLA} ${BORDE_PANTALLA} ${
          animated
            ? 'translate-y-0 opacity-100'
            : 'translate-y-full opacity-100 md:translate-y-1 md:opacity-0'
        }`}
        style={
          {
            '--sidebar-w': `${sidebarWidth}px`,
            ...vvStyle,
          } as React.CSSProperties
        }
      >
        <VeloSoltarImagen visible={arrastrando} />
        {/* Encabezado — sólo desktop, y sólo en el estado VACÍO: con la grilla puesta el
            breadcrumb se va a la pastilla flotante de más abajo, y dejarlo también acá
            sería decir dos veces dónde estás parado. En mobile el chrome del shell trae
            su back. */}
        {!activo && (
          <EncabezadoPantalla
            className="hidden md:flex items-center justify-between md:px-8 md:pt-6"
            migas={[{ label: 'Inicio', onClick: onClose }, { label: titulo }]}
          >
            {/* "Ver videos creados", arriba a la derecha, en el flujo del encabezado
                (el slot de acciones que ya tiene). Abre el historial en el estado
                activo, el mismo que se ve al generar
                (ADR-2026-09-19-el-creador-de-video-arranca-en-15-segundos-y-abre-sus-creaciones).
                Estuvo al pie como pastilla `absolute` dentro del scroller centrado y no se
                dejaba clickear bien: acá es un botón común, sin capas encima. */}
            <button
              type="button"
              onClick={onVerCreaciones}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[13.5px] font-medium text-[#02062BB3] hover:text-brand-black transition-colors"
            >
              <Film className="w-4 h-4" strokeWidth={2} />
              Ver videos creados
            </button>
          </EncabezadoPantalla>
        )}

        <div className="relative flex-1 min-h-0 flex flex-col pt-[calc(env(safe-area-inset-top,0px)+3.5rem)] md:pt-0">
          {activo ? (
            // ── DESKTOP ACTIVO: la grilla ocupa TODO y el chrome flota encima ──
            //
            // ADR-2026-09-08-el-chrome-del-creador-de-video-flota-sobre-la-grilla. Antes
            // eran tres franjas apiladas (encabezado / grilla / caja) y la grilla vivía en
            // el hueco del medio: en un modo cuya pantalla ES la grilla, dos de las tres
            // franjas eran marco. Ahora la grilla toma la caja entera y pasa por debajo de
            // las pastillas de arriba y de la caja de abajo, que quedan como capas.
            <div className="relative flex-1 min-h-0">
              {/* El scroller, a toda la caja. El padding —y no un margen ni un hueco del
                  layout— es lo que hace que la grilla se pueda scrollear ENTERA: arranca
                  debajo de las pastillas y termina arriba de la caja, pero al scrollear
                  las cards siguen pasando por atrás de las dos.

                  Las tres capas entran con `aparece-tras-esqueleto` (fundido + 6px de
                  subida, 420ms) escalonadas: es la transición del estado vacío al activo,
                  tanto al generar como al tocar "Ver videos creados"
                  (ADR-2026-09-19-el-creador-de-video-arranca-en-15-segundos-y-abre-sus-creaciones).
                  La clase no deja transform colgado, así que las capas `fixed` de adentro
                  (visor, menús) siguen resolviendo contra la ventana. */}
              <div
                className="aparece-tras-esqueleto absolute inset-0 overflow-y-auto px-8"
                style={{ paddingTop: ALTO_PILLS, paddingBottom: altoCaja }}
              >
                {columnaResultados}
              </div>

              {/* Capa de arriba: el breadcrumb y el filtro. `pointer-events-none` en la
                  capa y `auto` en cada pastilla (vía `PASTILLA_FLOTANTE`) para que el
                  hueco entre las dos no coma el scroll de la grilla. */}
              <div
                className="aparece-tras-esqueleto pointer-events-none absolute inset-x-8 top-4 z-20 flex items-start justify-between gap-3"
                style={{ animationDelay: '90ms' }}
              >
                <Migas
                  items={[{ label: 'Inicio', onClick: onClose }, { label: titulo }]}
                  className={`${PASTILLA_FLOTANTE} px-4 py-2`}
                />
                <FiltroPill valor={filtro} onChange={onFiltroChange} />
              </div>

              {/* Capa de abajo: la caja. Opaca a propósito —`PromptBoxLibre` ya es blanca
                  entera— así las cards desaparecen de golpe detrás de ella en vez de
                  desvanecerse a medias. La sombra va en el wrapper y no en la caja: la
                  caja es compartida con Prompt libre, donde va apoyada y no flotando. */}
              <div
                ref={cajaRef}
                className="aparece-tras-esqueleto pointer-events-none absolute inset-x-0 bottom-0 z-20 px-8 pb-6"
                style={{ animationDelay: '40ms' }}
              >
                <div className="pointer-events-auto max-w-3xl mx-auto flex flex-col items-center rounded-2xl shadow-[0_8px_30px_rgba(2,6,43,0.12)]">
                  {box}
                </div>
              </div>
            </div>
          ) : isMdUp ? (
            // ── DESKTOP VACÍO: el layout del asistente ───────────────────────
            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col px-8 items-center py-6">
              {/* `m-auto` y no `justify-center` en el scroller: con el catálogo de tipos
                  puesto el contenido pasa el alto de la ventana, y `justify-center`
                  recorta el principio —el título queda arriba del scroll y no se puede
                  llegar—. Con margen automático se centra mientras entra y se apoya
                  arriba cuando no. */}
              <div className="m-auto w-full flex flex-col items-center">
                <div className="flex items-center justify-center mb-8">{tituloGrande}</div>
                <div className="w-full max-w-3xl mx-auto flex flex-col items-center">
                  <div className="relative z-10 w-full flex justify-center">{box}</div>
                </div>
                <div className="w-full max-w-3xl mx-auto flex justify-center mt-8">
                  {cardsDesktop}
                </div>
                {/* Los tipos, abajo de todo: son opcionales (el default es General), así
                    que van DESPUÉS de lo que sí hay que hacer —escribir o adjuntar—. Sin
                    `max-w-3xl`: éste es el único bloque que usa la pantalla entera. */}
                <div className="w-full mt-8">{bloqueDeTipos('fila')}</div>
              </div>
            </div>
          ) : (
            // ── MOBILE: el asistente en video ────────────────────────────────
            <div
              className="flex-1 min-h-0 overflow-y-auto flex flex-col px-5"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)' }}
            >
              <div
                className={`flex-1 min-h-[120px] flex items-center justify-center transition-opacity duration-300 ${
                  inputFocused ? 'opacity-0' : 'opacity-100'
                }`}
              >
                {tituloGrande}
              </div>
              {/* Padre BLOCK a propósito: la tira de referencias se sale a los bordes. */}
              <div className="w-full">{cards}</div>
              <div className="mt-3">{box}</div>
              {/* Generar de ancho completo con el costo adentro — el mismo botón del
                  asistente en mobile, donde la flecha de la caja no existe. */}
              <button
                type="button"
                onClick={falta ? undefined : handleGenerar}
                disabled={!!falta || cargando}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-5 h-5" />
                <span>Generar video</span>
                <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold tabular-nums">
                  <img
                    src="/images/token_icon.png"
                    alt=""
                    aria-hidden="true"
                    className="w-3.5 h-3.5 object-contain"
                  />
                  {creditos}
                </span>
              </button>

              {/* Los tipos, abajo de todo, igual que en escritorio: son opcionales (el
                  default es General) así que van DESPUÉS de lo que sí hay que hacer
                  —escribir, adjuntar y generar—. En el teléfono esto queda abajo del
                  pliegue a propósito: el scroller ya existe y bajar es lo que descubre
                  que el catálogo está. */}
              <div className="mt-8">{bloqueDeTipos('angosta')}</div>
            </div>
          )}
        </div>
      </div>

      {/* Input de archivo oculto — "Subir imagen". */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          void subirImagen(f);
          e.target.value = '';
        }}
      />

      {/* Los pickers reales, los mismos del asistente. */}
      {picker === 'product' && (
        <ProductFullMenu
          initialSelected={[]}
          allowMulti
          onCancel={() => setPicker('none')}
          onAccept={agregarProductos}
        />
      )}
      <ModelPickerSheet
        open={picker === 'model'}
        selection={null}
        onChange={elegirModelo}
        onClose={() => setPicker('none')}
        userModels={userModels}
        userId={userId}
        companyId={companyId}
        onModelCreated={refreshModels}
        hideAuto
      />

      {/* El catálogo de tipos que abre el "+" del estado activo. Es el MISMO modal que
          abren la pill y la imagen anclada: portalea solo y se cierra al elegir. */}
      <MenuDeTipos
        abierto={tiposOpen}
        onCerrar={() => setTiposOpen(false)}
        valor={tipoVideo}
        onPick={(slug) => {
          setTipoVideo(slug);
          setTiposOpen(false);
        }}
      />

      {/* La vista ampliada de un adjunto — se abre tocando su chip
          (ADR-2026-09-08-los-adjuntos-de-la-caja-dicen-que-son-y-se-abren). Portalea al
          body por su cuenta, así que va suelta acá. */}
      <VisorDeAdjunto
        adjunto={adjuntoAbierto}
        onClose={() => setAdjuntoAbierto(null)}
        companyId={companyId}
      />

      {/* Sheet único de referencias — MOBILE. */}
      <ReferenciasSheet
        open={referenciasOpen}
        onClose={() => setReferenciasOpen(false)}
        attachments={adjuntos}
        onRemoveAttachment={quitarAdjunto}
        models={userModels}
        onUploadImage={() => fileInputRef.current?.click()}
        onUtilizar={({ products, model }) => {
          const elegidos = new Set(products.map((p) => p.id));
          setAdjuntos((prev) =>
            prev.filter((a) => a.kind !== 'product' || elegidos.has(a.productId)),
          );
          agregarProductos(products.map((p) => p.id));
          if (model) elegirModelo(model);
        }}
        uploading={subiendo}
      />

      {/* Mobile: la pantalla de carga de video (barra larga) y, al completar con fila,
          el salto a resultados. En desktop nunca se monta: la tanda se ve en la grilla. */}
      {cargando && (
        <LoadingAnimation
          message="Generando video..."
          subMessage="Esto puede tardar varios minutos"
          durationMs={duracionBarraMs}
          completeNow={completar}
          onCompleted={() => {
            setCargando(false);
            setCompletar(false);
            const pend = pendingNavRef.current;
            pendingNavRef.current = null;
            if (pend && companyId) {
              navigate(RUTA_RESULTADOS, {
                state: { imageIds: pend.imageIds, companyId },
              });
            }
          }}
        />
      )}
    </>,
    document.body,
  );
};

export default CreadorVideoSheet;
